// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Live inference: what the current readings say about how the plant is
 * running.
 *
 * The live half of the structure/live split (design §7.5), beside
 * `assetLiveState.ts`. Rebuilt on every poll and never moves geometry: every
 * result here is keyed by an asset or link id the structural model already
 * has, so a poll repaints in place.
 *
 * Rules, in the order the drawing needs them. Each names its falsifier, and
 * each falsifier is a test in `powerFlowLive.test.ts`.
 *
 * 1. **Energized** — a conductor is live when the asset it feeds reads voltage
 *    (above half its rated voltage), dead when that reading is near zero, and
 *    unknown otherwise. A source that publishes its own output voltage decides
 *    its own conductor: a genset at 0 V is not energizing anything however
 *    live the board it is wired to. An output reading is judged against the
 *    *output* side — the source's own output rating if the twin publishes
 *    one, else the target's rating, else the nearest standard system voltage —
 *    never against the source's input-side `ratedVoltage`: a 480 V PDU
 *    putting out 209 V is stepping down, not dying. A stale output reading
 *    decides nothing on its own (`unknown`); a stale zero is remembered only
 *    as a candidate for rule 3.
 * 2. **Flowing** — a conductor carries load when its source exports power over
 *    its one outgoing link, or when it is the only input and its target draws
 *    power, or — for a board with several inputs — when the target's draw
 *    exceeds what the other, metered inputs account for (the bypass residual).
 *    A source with several branches says nothing about which one carries its
 *    export; those links fall through to the target side.
 * 3. **Standby** — a non-utility source exporting nothing whose board is live
 *    from another, fresh, input. Frozen zeros still count here, flagged: a
 *    three-day-old zero is weak evidence, and the caption says so. Every link
 *    of a standby source is `standby` and carries no flow, whatever the target
 *    side said. A stale zero that never becomes standby stays `unknown`, never
 *    `dead`; a stale *positive* export asserts nothing at all, and neither does
 *    a fresh trickle under the idle threshold — only an export of zero can make
 *    a source standby.
 * 4. **Selected source** — on a board with two source-like inputs, the one
 *    whose conductor is flowing. Both flowing is reported, never resolved.
 * 5. **Loading** — live over rated, unit-matched: a kVA rating is compared with
 *    apparent power, a kW rating with active power, and a mismatch renders
 *    nothing rather than a wrong percentage. A series element with no meter is
 *    read off its one served child only when every other input to that child
 *    is dead or standby — an exclusive path — otherwise the child's draw is
 *    shared and none of it is this element's.
 * 6. **N+1** — an output board's draw against the total rated UPS capacity
 *    feeding it minus the largest unit: what remains if the biggest UPS drops.
 * 7. **Freshness** — a reading older than `staleAfterMs` never drives rules
 *    1, 2 or 4, and is reported per asset so the panel can grey it. A reading
 *    from the future is stale too.
 * 8. **Breakers** — a breaker folded onto a conductor (`link.via`) is `closed`
 *    when its own fresh current or power is non-zero: a device passing load
 *    is closed. A zero is *not* read as open — an idle closed breaker reads
 *    zero too — so the position stays `unknown`. Alarm is not decided here: the
 *    mark comes from the alarm service's open alarms (`breakerMarkState`), not
 *    from the twin's `AlarmStatus` point, which is being retired as a source.
 */

import type { Entity, Relationship } from '../types/digitalTwin';
import type { PowerFlowGraph, PowerFlowLink, PowerFlowVertex } from './powerFlowGraph';
import type { PowerFlowInference } from './powerFlowInference';
import {
  APPARENT_POWER_KINDS,
  FUEL_KINDS,
  INPUT_PHASE_CURRENT_KINDS,
  INPUT_VOLTAGE_KINDS,
  INPUT_VOLTAGE_LN_KINDS,
  LOAD_POWER_KINDS,
  OUTPUT_PHASE_CURRENT_KINDS,
  OUTPUT_POWER_KINDS,
  OUTPUT_VOLTAGE_KINDS,
  POWER_FACTOR_KINDS,
  RUN_STATUS_KINDS,
  SOC_KINDS,
  indexReadings,
  isFresh,
  readAll,
  readAny,
  readFresh,
  type Reading,
  type ReadingIndex,
} from './powerFlowReadings';
import { parseQuantity } from './quantity';
import { isMotorSymbol } from '../config/powerFlowSymbols';

export type Energization = 'live' | 'dead' | 'standby' | 'unknown';

/** What the readings say about one breaker on a conductor. Rule 8. */
export interface BreakerState {
  /**
   * `closed` on fresh non-zero current or power; otherwise `unknown`. `open`
   * is in the type for the drawing and the twin has yet to publish a position
   * point that could produce it — no rule here ever emits it.
   */
  position: 'closed' | 'open' | 'unknown';
}

export interface AssetLoading {
  /** Live figure in the rating's unit (kW or kVA). */
  live: number;
  rated: number;
  unit: 'kW' | 'kVA';
  /** `live / rated`. */
  ratio: number;
  /** `own` when the asset meters itself; `downstream` when read off its one child. */
  basis: 'own' | 'downstream';
}

export interface RedundancyCheck {
  boardId: string;
  /** The UPSs feeding the board, sorted. */
  upsIds: string[];
  /** Board draw, kW. */
  load: number;
  /** Sum of the UPS ratings, kW. */
  totalRating: number;
  /** The largest single UPS rating among them, kW. */
  largestRating: number;
  /** `totalRating - largestRating`: what carries the board if the largest unit drops. */
  spareCapacity: number;
  /** `load / spareCapacity`. */
  ratio: number;
  /** `ratio <= 1`. */
  holds: boolean;
}

export interface PowerFlowLiveOptions {
  /** Epoch ms "now", for freshness. Injected so tests are deterministic. */
  now?: number;
  /** Readings older than this drive no inference. Default: three minutes. */
  staleAfterMs?: number;
  /** Fraction of rating below which a draw counts as idle. Default 1 %. */
  idleFraction?: number;
  /** Absolute kW floor for "carrying load" when no rating is known. */
  idleKw?: number;
}

export interface PowerFlowLiveState {
  energizedOf: Map<string, Energization>;
  /** Breaker id → position, for every breaker on a drawn conductor. */
  breakerOf: Map<string, BreakerState>;
  /** Links whose standby/unknown verdict rests on a stale reading. */
  staleEvidenceLinkIds: Set<string>;
  /** Link id → epoch ms of the stale reading that verdict rests on. */
  staleEvidenceAtOf: Map<string, number>;
  /** Link id → carrying load. Absent when undecidable. */
  flowingOf: Map<string, boolean>;
  /** Asset id → running (sources and motors). Absent when nothing says. */
  runningOf: Map<string, boolean>;
  /**
   * Ids whose `runningOf` was read off export or draw rather than a published
   * run-status point. The drawing marks these as inferred.
   */
  runInferredIds: Set<string>;
  standbyIds: Set<string>;
  /** Board id → inputs currently carrying load, for boards with a choice. */
  selectedSourceOf: Map<string, string[]>;
  loadingOf: Map<string, AssetLoading>;
  redundancyOf: Map<string, RedundancyCheck>;
  /** Asset id → worst phase deviation from the mean, percent. */
  phaseImbalanceOf: Map<string, number>;
  /** Asset id → `480Y/277 V`, `480 → 208Y/120 V`. */
  voltageSystemOf: Map<string, string>;
  /** Asset id → draw in kW (active power). */
  powerKwOf: Map<string, number>;
  /**
   * Asset id → line-to-line volts, and mean line current in amps.
   *
   * Exposed as scalars because the one-line draws a meter on the bus it
   * measures — `PQM1A · 480 V · 8.5 MW · 3,898 A` — rather than listing
   * measurements inside the asset's box.
   */
  voltsOf: Map<string, number>;
  ampsOf: Map<string, number>;
  /** Asset id → power factor, for the metering strip. */
  powerFactorOf: Map<string, number>;
  /** Asset id → export in kW, for sources and UPSs that publish it. */
  exportKwOf: Map<string, number>;
  socOf: Map<string, number>;
  fuelOf: Map<string, number>;
  /** Asset id → quantity kinds whose reading is stale. */
  staleKindsOf: Map<string, string[]>;
  freshReadingCount: number;
  staleReadingCount: number;
  /** Epoch ms of the oldest stale reading, or undefined. */
  oldestStaleAt?: number;
}

/** Readings that say a breaker is passing load: any phase current, or power. */
const BREAKER_LOAD_KINDS: readonly string[] = [
  ...INPUT_PHASE_CURRENT_KINDS,
  ...OUTPUT_PHASE_CURRENT_KINDS,
  ...LOAD_POWER_KINDS,
];

export const DEFAULT_STALE_AFTER_MS = 3 * 60 * 1000;

export const EMPTY_LIVE: PowerFlowLiveState = {
  energizedOf: new Map(),
  breakerOf: new Map(),
  staleEvidenceLinkIds: new Set(),
  staleEvidenceAtOf: new Map(),
  flowingOf: new Map(),
  runningOf: new Map(),
  runInferredIds: new Set(),
  standbyIds: new Set(),
  selectedSourceOf: new Map(),
  loadingOf: new Map(),
  redundancyOf: new Map(),
  phaseImbalanceOf: new Map(),
  voltageSystemOf: new Map(),
  powerKwOf: new Map(),
  voltsOf: new Map(),
  ampsOf: new Map(),
  powerFactorOf: new Map(),
  exportKwOf: new Map(),
  socOf: new Map(),
  fuelOf: new Map(),
  staleKindsOf: new Map(),
  freshReadingCount: 0,
  staleReadingCount: 0,
};

/** Standard system voltages a measured L-L value is snapped to, volts. */
const SYSTEMS: { ll: number; label: string }[] = [
  { ll: 208, label: '208Y/120 V' },
  { ll: 240, label: '240 V' },
  { ll: 400, label: '400Y/230 V' },
  { ll: 415, label: '415Y/240 V' },
  { ll: 480, label: '480Y/277 V' },
  { ll: 600, label: '600Y/347 V' },
  { ll: 4160, label: '4.16 kV' },
  { ll: 12470, label: '12.47 kV' },
  { ll: 13800, label: '13.8 kV' },
];

/** A voltage within this fraction of a standard is that standard. */
const SYSTEM_TOLERANCE = 0.08;

/**
 * Attribute names a twin might use for a transforming asset's output-side
 * rating. GA-10 publishes none of them — every PDU carries only its 480 V
 * input `ratedVoltage` — so these are the plausible spellings, tried in order.
 */
const OUTPUT_VOLTAGE_RATING_KEYS = [
  'ratedOutputVoltage',
  'outputVoltage',
  'ratedSecondaryVoltage',
  'secondaryVoltage',
  'outputRatedVoltage',
] as const;

/** Absolute floor, volts, below which an unrated reading is "nothing". */
const DEAD_VOLTS = 50;

const toKw = (reading: Reading): number | undefined => {
  if (reading.family === 'W') return reading.base / 1000;
  // A bare number with no unit: the twin's power points are kW in every
  // payload we hold, and the alternative is discarding the reading.
  if (reading.family === 'unknown') return reading.base;
  return undefined;
};

const toKva = (reading: Reading): number | undefined => {
  if (reading.family === 'VA') return reading.base / 1000;
  if (reading.family === 'unknown') return reading.base;
  return undefined;
};

const toVolts = (reading: Reading): number | undefined => {
  if (reading.family === 'V' || reading.family === 'unknown') return reading.base;
  return undefined;
};

const nearestSystem = (volts: number): { ll: number; label: string } | undefined => {
  let best: { ll: number; label: string } | undefined;
  let bestError = Number.POSITIVE_INFINITY;
  for (const system of SYSTEMS) {
    const error = Math.abs(volts - system.ll) / system.ll;
    if (error < bestError) {
      bestError = error;
      best = system;
    }
  }
  return bestError <= SYSTEM_TOLERANCE ? best : undefined;
};

/**
 * Derive the live state.
 *
 * `graph` supplies roles and links; `inference` supplies the boards with a
 * choice of source and the bypass links. Both are structural and already
 * memoised by the page, so this pass is one walk over links and one over
 * vertices per poll.
 */
export const derivePowerFlowLive = (
  entities: readonly Entity[],
  relationships: readonly Relationship[],
  graph: PowerFlowGraph,
  inference: PowerFlowInference,
  options: PowerFlowLiveOptions = {}
): PowerFlowLiveState => {
  const now = options.now ?? Date.now();
  const staleAfterMs = options.staleAfterMs ?? DEFAULT_STALE_AFTER_MS;
  const idleFraction = options.idleFraction ?? 0.01;
  const idleKw = options.idleKw ?? 1;

  const entitiesById = new Map(entities.map(entity => [entity.id, entity]));
  const readings = indexReadings(entities, relationships);
  const { vertices, verticesById, links } = graph;

  const fresh = (reading: Reading | undefined): Reading | undefined =>
    reading && isFresh(reading, now, staleAfterMs) ? reading : undefined;
  /** The first fresh reading among `kinds`: a stale preferred phase never hides a fresh one. */
  const freshAny = (id: string, kinds: readonly string[]): Reading | undefined =>
    readFresh(readings, id, kinds, now, staleAfterMs);

  const ratedOf = (id: string, key: string) =>
    parseQuantity(entitiesById.get(id)?.attributes?.[key]);

  const voltsAttribute = (id: string, key: string): number | undefined => {
    const rated = ratedOf(id, key);
    return rated && (rated.family === 'V' || rated.family === 'unknown') && rated.base > 0
      ? rated.base
      : undefined;
  };

  const ratedVolts = (id: string): number | undefined => voltsAttribute(id, 'ratedVoltage');

  const ratedOutputVolts = (id: string): number | undefined => {
    for (const key of OUTPUT_VOLTAGE_RATING_KEYS) {
      const volts = voltsAttribute(id, key);
      if (volts !== undefined) return volts;
    }
    return undefined;
  };

  /**
   * Is a source's measured output voltage energizing the run to `targetId`?
   *
   * The nominal is the output side: the source's own output rating, else what
   * the target is rated for, else the nearest standard system the reading
   * snaps to (a reading that is a standard voltage is live by definition),
   * else the absolute floor. The source's input-side `ratedVoltage` is never
   * the basis — for a step-down PDU it would call a healthy 209 V output dead.
   */
  const outputEnergizes = (sourceId: string, targetId: string, measured: number): boolean => {
    const nominal = ratedOutputVolts(sourceId) ?? ratedVolts(targetId);
    if (nominal !== undefined) return measured >= nominal * 0.5;
    if (measured > 0 && nearestSystem(measured)) return true;
    return measured >= DEAD_VOLTS;
  };

  /** kW below which a draw is "idle" for this asset. */
  const idleThresholdKw = (id: string): number => {
    const rated = ratedOf(id, 'ratedPower');
    if (rated && (rated.family === 'W' || rated.family === 'VA')) {
      return Math.max(idleKw, (rated.base / 1000) * idleFraction);
    }
    return idleKw;
  };

  // --- Per-asset scalar readings ------------------------------------------
  const powerKwOf = new Map<string, number>();
  const voltsOf = new Map<string, number>();
  const ampsOf = new Map<string, number>();
  const powerFactorOf = new Map<string, number>();
  const exportKwOf = new Map<string, number>();
  const socOf = new Map<string, number>();
  const fuelOf = new Map<string, number>();
  const runningOf = new Map<string, boolean>();
  const runInferredIds = new Set<string>();
  /** Source id → epoch ms of the stale zero export its `running = false` rests on. */
  const staleZeroExportAtOf = new Map<string, number>();
  const phaseImbalanceOf = new Map<string, number>();
  const voltageSystemOf = new Map<string, string>();
  const loadingOf = new Map<string, AssetLoading>();
  /** Series elements with no meter of their own, read off their child once link verdicts exist. */
  const deferredLoading: { id: string; child: string; unit: 'kW' | 'kVA'; ratedK: number }[] = [];

  for (const vertex of vertices) {
    const id = vertex.id;

    const load = freshAny(id, LOAD_POWER_KINDS);
    const loadKw = load ? toKw(load) : undefined;
    if (loadKw !== undefined) powerKwOf.set(id, loadKw);

    const exported = freshAny(id, OUTPUT_POWER_KINDS);
    const exportKw = exported ? toKw(exported) : undefined;
    if (exportKw !== undefined) exportKwOf.set(id, exportKw);

    // Meter figures: L-L volts, and the mean of the three line currents so a
    // single number stands for the run the way a panel meter reports it.
    const volts = freshAny(id, INPUT_VOLTAGE_KINDS);
    const voltsValue = volts ? toVolts(volts) : undefined;
    // A fresh measured 0 V is data — a dead bus reads zero — so it is kept.
    if (voltsValue !== undefined && voltsValue >= 0) voltsOf.set(id, voltsValue);
    const phases = readAll(readings, id, INPUT_PHASE_CURRENT_KINDS)
      .map(fresh)
      .filter((r): r is Reading => r !== undefined);
    if (phases.length > 0) {
      ampsOf.set(id, phases.reduce((sum, r) => sum + r.base, 0) / phases.length);
    }

    const pf = freshAny(id, POWER_FACTOR_KINDS);
    // Closed interval: an energized but idle board genuinely reads a power
    // factor of 0, and dropping it made the meter report no PF at all rather
    // than the zero it measured — the same rule the volts above already follow.
    if (pf && pf.base >= 0 && pf.base <= 1) powerFactorOf.set(id, pf.base);

    const soc = freshAny(id, SOC_KINDS);
    if (soc) socOf.set(id, soc.base);
    const fuel = freshAny(id, FUEL_KINDS);
    if (fuel) fuelOf.set(id, fuel.base);

    // Running: a source by what it exports — a zero is allowed to be frozen
    // (rule 3, flagged), but only a fresh positive export asserts running; a
    // stale positive one asserts nothing. A motor by its run status or its draw.
    const rawExport = readAny(readings, id, OUTPUT_POWER_KINDS);
    const rawExportKw = rawExport ? toKw(rawExport) : undefined;
    const run = freshAny(id, RUN_STATUS_KINDS);
    let running: boolean | undefined;
    if (vertex.role === 'source' && rawExport && rawExportKw !== undefined) {
      if (rawExportKw <= 0) {
        // Only a zero export is "not running": any P > 0, however small, is a
        // source doing something, and must never be called standby (rule 3).
        running = false;
        if (!isFresh(rawExport, now, staleAfterMs)) staleZeroExportAtOf.set(id, rawExport.at);
      } else if (isFresh(rawExport, now, staleAfterMs) && rawExportKw > idleThresholdKw(id)) {
        running = true;
      }
      // A fresh trickle under the idle threshold, or a stale positive, decides nothing.
    }
    if (running !== undefined) {
      // Decided above, off the export: an inference about a source.
      runInferredIds.add(id);
    } else if (run) {
      running = run.base > 0;
    } else if (isMotorSymbol(vertex.symbol) && loadKw !== undefined) {
      running = loadKw > idleThresholdKw(id);
      runInferredIds.add(id);
    }
    if (running !== undefined) runningOf.set(id, running);

    // Phase imbalance: worst deviation from the mean, percent.
    for (const kinds of [INPUT_PHASE_CURRENT_KINDS, OUTPUT_PHASE_CURRENT_KINDS]) {
      const phases = readAll(readings, id, kinds)
        .map(fresh)
        .filter((r): r is Reading => r !== undefined);
      if (phases.length !== 3) continue;
      const mean = phases.reduce((sum, r) => sum + r.base, 0) / 3;
      if (mean < 1) continue;
      const worst = Math.max(...phases.map(r => Math.abs(r.base - mean)));
      phaseImbalanceOf.set(id, (100 * worst) / mean);
      break;
    }

    // Voltage system, from measured L-L (and L-N, and output).
    const inLL = freshAny(id, INPUT_VOLTAGE_KINDS.slice(0, 3));
    const inVolts = inLL ? toVolts(inLL) : undefined;
    const inSystem = inVolts !== undefined && inVolts > 0 ? nearestSystem(inVolts) : undefined;
    const outLL = freshAny(id, OUTPUT_VOLTAGE_KINDS.slice(0, 3));
    const outVolts = outLL ? toVolts(outLL) : undefined;
    const outSystem = outVolts !== undefined && outVolts > 0 ? nearestSystem(outVolts) : undefined;
    if (inSystem && outSystem && inSystem.ll !== outSystem.ll) {
      voltageSystemOf.set(id, `${inSystem.ll} → ${outSystem.label}`);
    } else if (inSystem) {
      const ln = freshAny(id, INPUT_VOLTAGE_LN_KINDS);
      // The Y form is only claimed when an L-N reading confirms a neutral.
      voltageSystemOf.set(id, ln ? inSystem.label : `${inSystem.ll} V`);
    } else if (outSystem) {
      voltageSystemOf.set(id, outSystem.label);
    }

    // Loading, unit-matched.
    const rated = ratedOf(id, 'ratedPower');
    if (rated && (rated.family === 'W' || rated.family === 'VA') && rated.base > 0) {
      const unit = rated.family === 'W' ? 'kW' : 'kVA';
      const ratedK = rated.base / 1000;
      let live: number | undefined;
      let basis: AssetLoading['basis'] = 'own';
      if (unit === 'kW') {
        // A UPS or a source is loaded by what it delivers, everything else by
        // what it draws.
        live =
          (vertex.symbol === 'ups' || vertex.role === 'source') && exportKw !== undefined
            ? exportKw
            : vertex.role === 'source'
              ? undefined
              : loadKw;
      } else {
        const apparent = freshAny(id, APPARENT_POWER_KINDS);
        live = apparent ? toKva(apparent) : undefined;
      }
      // A transformer meters nothing itself; the one asset it serves does. A
      // link to another transformer is the MV riser looping through, not a
      // load on this unit, so it does not count as a child here.
      const served = vertex.out.filter(
        child =>
          !(vertex.symbol === 'transformer' && verticesById.get(child)?.symbol === 'transformer')
      );
      // Only a series element is read off what it serves, and only when the
      // path is exclusive: a child with a second live input splits its draw
      // across both, and none of it can be attributed here. Whether the other
      // inputs are live is a link verdict, so this is settled after the
      // energization and standby passes below. A source that meters nothing
      // stays unknown: its board's draw is not its output.
      if (live === undefined && vertex.role === 'series' && served.length === 1) {
        deferredLoading.push({ id, child: served[0], unit, ratedK });
      }
      if (live !== undefined) {
        loadingOf.set(id, { live, rated: ratedK, unit, ratio: live / ratedK, basis });
      }
    }
  }

  // --- Per-link energization and flow --------------------------------------
  const energizedOf = new Map<string, Energization>();
  const staleEvidenceLinkIds = new Set<string>();
  const staleEvidenceAtOf = new Map<string, number>();
  const flowingOf = new Map<string, boolean>();
  /** Links a fresh output-voltage reading of their own source called live. */
  const liveFromOwnOutput = new Set<string>();

  const recordStaleEvidence = (linkId: string, at: number) => {
    staleEvidenceLinkIds.add(linkId);
    const existing = staleEvidenceAtOf.get(linkId);
    if (existing === undefined || at < existing) staleEvidenceAtOf.set(linkId, at);
  };

  const inLinksOf = new Map<string, PowerFlowLink[]>();
  for (const link of links) {
    const list = inLinksOf.get(link.target);
    if (list) list.push(link);
    else inLinksOf.set(link.target, [link]);
  }

  const targetVoltageVerdict = (target: PowerFlowVertex): Energization => {
    const reading = freshAny(target.id, INPUT_VOLTAGE_KINDS);
    const volts = reading ? toVolts(reading) : undefined;
    if (volts === undefined) {
      // No voltage point. A measured draw implies a live conductor.
      const draw = powerKwOf.get(target.id);
      return draw !== undefined && draw > idleThresholdKw(target.id) ? 'live' : 'unknown';
    }
    const nominal = ratedVolts(target.id);
    const threshold = nominal ? nominal * 0.5 : 50;
    return volts >= threshold ? 'live' : 'dead';
  };

  // First pass: verdicts from the target side and from sources that meter
  // their own output.
  for (const link of links) {
    const source = verticesById.get(link.source);
    const target = verticesById.get(link.target);
    if (!source || !target) continue;

    const rawOutV = readAny(readings, source.id, OUTPUT_VOLTAGE_KINDS);
    const outV = rawOutV ? toVolts(rawOutV) : undefined;
    if (rawOutV && outV !== undefined) {
      const energizes = outputEnergizes(source.id, target.id, outV);
      if (fresh(rawOutV)) {
        // A source reading zero output is not energizing this run. Decided
        // below whether that makes it standby or dead.
        energizedOf.set(link.id, energizes ? 'live' : 'dead');
        if (energizes) liveFromOwnOutput.add(link.id);
      } else {
        // Stale: no verdict of its own. A stale zero is kept as evidence for
        // the standby pass, and is never promoted to `dead`.
        energizedOf.set(link.id, 'unknown');
        if (!energizes) recordStaleEvidence(link.id, rawOutV.at);
      }
      continue;
    }
    energizedOf.set(link.id, targetVoltageVerdict(target));
  }

  // Standby: a non-utility source exporting nothing, whose board is live from
  // some other input (`live` is only ever set from a fresh reading). Every
  // one of its conductors becomes standby — whether the first pass called it
  // dead, unknown from a stale zero, or live off the shared board — except a
  // run the source's own fresh output voltage showed energized. Settled before
  // flow, so a standby run carries nothing and accounts for nothing in a
  // sibling's residual.
  const standbyIds = new Set<string>();
  for (const vertex of vertices) {
    if (vertex.role !== 'source' || vertex.symbol === 'utility') continue;
    if (runningOf.get(vertex.id) !== false) continue;
    const ownLinks = links.filter(link => link.source === vertex.id);
    const boardLiveElsewhere = ownLinks.some(link =>
      (inLinksOf.get(link.target) ?? []).some(
        other => other.id !== link.id && energizedOf.get(other.id) === 'live'
      )
    );
    if (!boardLiveElsewhere) continue;
    standbyIds.add(vertex.id);
    const staleZeroAt = staleZeroExportAtOf.get(vertex.id);
    for (const link of ownLinks) {
      if (liveFromOwnOutput.has(link.id)) continue;
      energizedOf.set(link.id, 'standby');
      if (staleZeroAt !== undefined) recordStaleEvidence(link.id, staleZeroAt);
    }
  }

  // Loading read off a child, now that every other input to that child has a
  // verdict. The path is exclusive when the other inputs are dead or standby:
  // the transformer beside a standby genset carries the whole board; beside a
  // second live source it carries an unknown share, and shows nothing.
  for (const { id, child, unit, ratedK } of deferredLoading) {
    const others = (inLinksOf.get(child) ?? []).filter(link => link.source !== id);
    const exclusive = others.every(link => {
      const verdict = energizedOf.get(link.id);
      return verdict === 'dead' || verdict === 'standby';
    });
    if (!exclusive) continue;
    const childReading =
      unit === 'kW' ? freshAny(child, LOAD_POWER_KINDS) : freshAny(child, APPARENT_POWER_KINDS);
    const live = childReading
      ? unit === 'kW'
        ? toKw(childReading)
        : toKva(childReading)
      : undefined;
    if (live === undefined) continue;
    loadingOf.set(id, { live, rated: ratedK, unit, ratio: live / ratedK, basis: 'downstream' });
  }

  // Flow. Source export first; then sole-input targets; then the residual.
  for (const link of links) {
    const source = verticesById.get(link.source);
    const target = verticesById.get(link.target);
    if (!source || !target) continue;
    const energized = energizedOf.get(link.id);
    if (energized === 'dead' || energized === 'standby') {
      flowingOf.set(link.id, false);
      continue;
    }

    // A source's export names the load on its run only when there is one run
    // to carry it; with several branches it says nothing about which.
    const sourceExport = exportKwOf.get(source.id);
    if (sourceExport !== undefined && source.out.length === 1) {
      flowingOf.set(link.id, sourceExport > idleThresholdKw(source.id));
      continue;
    }

    const targetDraw = powerKwOf.get(target.id);
    if (targetDraw === undefined) continue;

    const siblings = (inLinksOf.get(target.id) ?? []).filter(other => other.id !== link.id);
    if (siblings.length === 0) {
      flowingOf.set(link.id, targetDraw > idleThresholdKw(target.id));
      continue;
    }

    // Residual: what the other inputs do not account for. Only decidable when
    // every other input is metered (exports), known dead, or on standby.
    let accounted = 0;
    let decidable = true;
    for (const sibling of siblings) {
      const siblingExport = exportKwOf.get(sibling.source);
      const siblingState = energizedOf.get(sibling.id);
      if (siblingExport !== undefined) accounted += siblingExport;
      else if (siblingState === 'dead' || siblingState === 'standby') accounted += 0;
      else decidable = false;
    }
    if (!decidable) continue;
    const residual = targetDraw - accounted;
    flowingOf.set(link.id, residual > Math.max(idleKw, targetDraw * idleFraction));
  }

  // Selected source, on boards with a choice.
  const selectedSourceOf = new Map<string, string[]>();
  for (const [boardId, sources] of Array.from(inference.sourceSelectionOf.entries())) {
    const selected = sources.filter(sourceId => flowingOf.get(`${sourceId}->${boardId}`) === true);
    selectedSourceOf.set(boardId, selected);
  }

  // N+1 on every output board: the load against what is left when the
  // largest unit drops.
  const redundancyOf = new Map<string, RedundancyCheck>();
  for (const boardId of inference.outputBoardIds) {
    const board = verticesById.get(boardId);
    if (!board) continue;
    const upsIds = board.in.filter(id => verticesById.get(id)?.symbol === 'ups').sort();
    if (upsIds.length < 2) continue;
    const ratings = upsIds
      .map(id => ratedOf(id, 'ratedPower'))
      .filter((q): q is NonNullable<typeof q> => !!q && q.family === 'W' && q.base > 0)
      .map(q => q.base / 1000);
    if (ratings.length !== upsIds.length) continue;
    const totalRating = ratings.reduce((sum, r) => sum + r, 0);
    const largestRating = Math.max(...ratings);
    const spareCapacity = totalRating - largestRating;
    if (spareCapacity <= 0) continue;
    const exports = upsIds.map(id => exportKwOf.get(id));
    const load =
      powerKwOf.get(boardId) ??
      (exports.every(e => e !== undefined)
        ? (exports as number[]).reduce((sum, e) => sum + e, 0)
        : undefined);
    if (load === undefined) continue;
    const ratio = load / spareCapacity;
    redundancyOf.set(boardId, {
      boardId,
      upsIds,
      load,
      totalRating,
      largestRating,
      spareCapacity,
      ratio,
      holds: ratio <= 1,
    });
  }

  // Freshness, per asset and overall.
  const staleKindsOf = new Map<string, string[]>();
  let freshReadingCount = 0;
  let staleReadingCount = 0;
  let oldestStaleAt: number | undefined;
  for (const vertex of vertices) {
    const perAsset = readings.get(vertex.id);
    if (!perAsset) continue;
    const stale: string[] = [];
    for (const [kind, reading] of Array.from(perAsset.entries())) {
      if (Number.isNaN(reading.at)) continue;
      if (isFresh(reading, now, staleAfterMs)) {
        freshReadingCount += 1;
      } else {
        staleReadingCount += 1;
        stale.push(kind);
        if (oldestStaleAt === undefined || reading.at < oldestStaleAt) oldestStaleAt = reading.at;
      }
    }
    if (stale.length > 0) staleKindsOf.set(vertex.id, stale.sort());
  }

  // Rule 8: breakers on the drawn conductors. Any fresh non-zero phase current
  // or power — a breaker passing load is closed whatever else it says, and a
  // fresh zero on one phase must not hide load on another. Alarms are not read
  // here: the mark comes from the alarm service (see `breakerMarkState`), never
  // from the twin's `AlarmStatus` point.
  const breakerOf = new Map<string, BreakerState>();
  for (const link of links) {
    for (const breakerId of link.via ?? []) {
      if (breakerOf.has(breakerId)) continue;
      const passing = readAll(readings, breakerId, BREAKER_LOAD_KINDS).some(
        reading => isFresh(reading, now, staleAfterMs) && reading.base !== 0
      );
      breakerOf.set(breakerId, { position: passing ? 'closed' : 'unknown' });
    }
  }

  return {
    energizedOf,
    breakerOf,
    staleEvidenceLinkIds,
    staleEvidenceAtOf,
    flowingOf,
    runningOf,
    runInferredIds,
    standbyIds,
    selectedSourceOf,
    loadingOf,
    redundancyOf,
    phaseImbalanceOf,
    voltageSystemOf,
    powerKwOf,
    voltsOf,
    ampsOf,
    powerFactorOf,
    exportKwOf,
    socOf,
    fuelOf,
    staleKindsOf,
    freshReadingCount,
    staleReadingCount,
    oldestStaleAt,
  };
};

export type { ReadingIndex };
